import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  HardHat, 
  GraduationCap, 
  Heart, 
  Factory, 
  Fuel, 
  ShoppingCart, 
  Laptop, 
  Leaf, 
  Stethoscope,
  ArrowRight,
  Users,
  TrendingUp,
  Target
} from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';

const IndustriesPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const industries = [
    {
      id: 'banking-financial-services',
      title: 'Banking / Financial Services',
      description: 'AI-powered solutions for loan automation, fraud detection, and personalized customer experiences in modern financial services.',
      icon: Building2,
      gradient: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-400',
      image: '/assets/Industries/bank-industry.png',
      solutions: ['Agentic Loan Recommendation', 'ManageRTC', 'Project Management'],
      keyBenefits: ['Real-time Risk Assessment', 'Automated Compliance', 'Enhanced Security']
    },
    {
      id: 'construction',
      title: 'Construction',
      description: 'Smart technologies for construction safety, equipment monitoring, and project management in the digital construction era.',
      icon: HardHat,
      gradient: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-500/10',
      textColor: 'text-orange-400',
      image: '/assets/Industries/construction-1.png',
      solutions: ['Equipment Failure Detection', 'PPE Compliance Monitoring', 'Agentic Loan Recommendation'],
      keyBenefits: ['Real-time Monitoring', 'Enhanced Worker Safety', 'Predictive Maintenance']
    },
    {
      id: 'education',
      title: 'Education',
      description: 'Intelligent technology solutions for personalized learning, content management, and administrative efficiency in educational institutions.',
      icon: GraduationCap,
      gradient: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-400',
      image: '/assets/Industries/education.png',
      solutions: [ 'Websites', 'Project Management Tool'],
      keyBenefits: ['Adaptive Learning', 'Enhanced Productivity', 'Digital Infrastructure']
    },
    {
      id: 'healthcare',
      title: 'Healthcare',
      description: 'Innovative healthcare systems for patient safety, compliance monitoring, and operational efficiency in medical facilities.',
      icon: Heart,
      gradient: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-500/10',
      textColor: 'text-emerald-400',
      image: '/assets/Industries/healthcareindustry.png',
      solutions: ['PPE Compliance Monitoring', 'Project Management Tool', 'ManageRTC', 'Websites'],
      keyBenefits: ['Safety-First Technology', 'Compliance Without Complexity', 'Streamlined Operations']
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      description: 'Smart manufacturing solutions for predictive maintenance, workforce management, and compliance in Industry 4.0 environments.',
      icon: Factory,
      gradient: 'from-gray-500 to-slate-600',
      bgColor: 'bg-gray-500/10',
      textColor: 'text-gray-400',
      image: '/assets/Industries/manufacture-industry.png',
      solutions: ['Equipment Failure Detection', 'ManageRTC', 'PPE Compliance Monitoring', 'DEG Compliance'],
      keyBenefits: ['Predictive Maintenance', 'Improved Uptime', 'Enhanced Safety']
    },
    {
      id: 'oil-gas',
      title: 'Oil & Gas',
      description: 'Digital energy solutions for asset monitoring, safety compliance, and operational efficiency in high-risk energy environments.',
      icon: Fuel,
      gradient: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-yellow-500/10',
      textColor: 'text-yellow-400',
      image: '/assets/Industries/Oil-and-gas.png',
      solutions: ['Equipment Failure Detection', 'ManageRTC', 'PPE Compliance Monitoring', 'DEG Compliance'],
      keyBenefits: ['Real-Time Risk Management', 'Built for Harsh Environments', 'Compliance Simplified']
    },
    {
      id: 'retail-ecommerce',
      title: 'Retail / E-commerce',
      description: 'AI-powered retail solutions for customer insights, review analysis, and digital commerce optimization.',
      icon: ShoppingCart,
      gradient: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500/10',
      textColor: 'text-pink-400',
      image: '/assets/Industries/e-commerce-1.png',
      solutions: [ 'Websites', 'Customer Review Summarizer'],
      keyBenefits: ['Customer-Centric Intelligence', 'Speed to Market', 'Omnichannel Optimization']
    },
    {
      id: 'technology-it-services',
      title: 'Technology / IT Services',
      description: 'Next-generation digital solutions for IT infrastructure, project management, and workforce optimization in tech companies.',
      icon: Laptop,
      gradient: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-500/10',
      textColor: 'text-cyan-400',
      image: '/assets/Industries/tech-office.png',
      solutions: ['ManageRTC', 'Websites', 'Project Management Tool'],
      keyBenefits: ['Developer-First Design', 'Streamlined Operations', 'Agile-Ready Infrastructure']
    },
    {
      id: 'green-hydrogen',
      title: 'Green Hydrogen',
      description: 'Sustainable energy solutions for green hydrogen production, monitoring, and optimization through advanced digital platforms.',
      icon: Leaf,
      gradient: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400',
      image: '/assets/Industries/green-hydrogen.png',
      solutions: ['Water Level IoT', 'Equipment Failure Detection', 'PPE Compliance Monitoring'],
      keyBenefits: ['Sustainable Operations', 'Predictive Analytics', 'Safety & Compliance']
    },
    {
      id: 'medical-coding',
      title: 'Medical Coding',
      description: 'AI-powered medical coding solutions for healthcare billing accuracy, compliance, and revenue cycle optimization.',
      icon: Stethoscope,
      gradient: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-500/10',
      textColor: 'text-indigo-400',
      image: '/assets/Industries/medical-coding-1.png',
      solutions: ['Medical Coding Platform'],
      keyBenefits: ['AI-Powered Efficiency', 'Accurate & Compliant', 'Domain Expertise']
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-10"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 12 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-primary rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 4,
                delay: i * 0.3,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl lg:text-6xl font-bold mb-6"
            >
              Industries We <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Transform</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-muted-foreground text-center max-w-4xl mx-auto leading-relaxed"
            >
              At amasQIS.ai, we deliver intelligent solutions across diverse industries, helping organizations 
              modernize operations, enhance efficiency, and drive digital transformation through AI, IoT, and advanced IT systems.
            </motion.p>
          </motion.div>

          {/* Industry Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid md:grid-cols-3 gap-8 mb-20"
          >
            <div className="glass p-6 rounded-xl text-center">
              <div className="flex items-center justify-center mb-3">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <div className="text-3xl font-bold text-primary mb-2">10+</div>
              <div className="text-muted-foreground">Industries Served</div>
            </div>
            <div className="glass p-6 rounded-xl text-center">
              <div className="flex items-center justify-center mb-3">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <div className="text-3xl font-bold text-accent mb-2">500+</div>
              <div className="text-muted-foreground">Global Clients</div>
            </div>
            <div className="glass p-6 rounded-xl text-center">
              <div className="flex items-center justify-center mb-3">
                <TrendingUp className="w-8 h-8 text-secondary" />
              </div>
              <div className="text-3xl font-bold text-secondary mb-2">98%</div>
              <div className="text-muted-foreground">Success Rate</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.id}
                  id={industry.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="group relative scroll-mt-20"
                >
                  <Link to={`/industries/${industry.id}`}>
                    <div className="glass h-full rounded-xl overflow-hidden hover:glass-strong transition-all duration-300">
                      {/* Industry Image */}
                      <div className="relative h-48 overflow-hidden">
                        <ImageWithFallback
                          src={industry.image}
                          alt={industry.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                          <div className="absolute top-4 left-4">
                            <div className={`p-3 rounded-lg ${industry.bgColor} backdrop-blur-sm`}>
                              <Icon className={`w-6 h-6 ${industry.textColor}`} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                          {industry.title}
                        </h3>
                        <p className="text-muted-foreground mb-4 line-clamp-3">
                          {industry.description}
                        </p>

                        {/* Key Benefits */}
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-accent mb-2">Key Benefits:</h4>
                          <div className="flex flex-wrap gap-1">
                            {industry.keyBenefits.slice(0, 2).map((benefit) => (
                              <span
                                key={benefit}
                                className="px-2 py-1 text-xs bg-primary/20 text-primary rounded-full"
                              >
                                {benefit}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Solutions Count */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {industry.solutions.length} Solutions Available
                          </span>
                          <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Transform Your Industry?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Discover how amasQIS.ai can revolutionize your operations with intelligent solutions tailored to your industry's unique challenges and opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-primary to-secondary px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-primary/25 transition-all duration-300"
              >
                Start Your Transformation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { IndustriesPage };